import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';
import { Virtuoso } from 'react-virtuoso';
import { ContactListItem } from '@/components/contacts/contact-list/contact-list-item';
import { type SortFriendReducerFilterType, sortFriendByFilterType } from '@/hooks/contacts/use-sorted-friends';
import useConversationSessions from '@/hooks/sessions/use-conversation-sessions';
import type { Friend } from '@/models/user/friend';
import ROUTES from '@/routes';
import { handleDisplaySessionMessages } from '@/routes/messages/sessions/_layout.loader';
import { useLayoutStore } from '@/stores/layout-status';
import { addTranslateToast } from '@/stores/toast';
import cn from '@/utils/merge-class-names';
import SortContactDropdown from './sort-contact-dropdown';

interface Props {
  friends: Friend[];
  selectedFriendIds?: number[];
  onSelectFriends?: (friendIds: number[]) => void;
  singleSelect?: boolean;
  disabledUserIds?: number[];
  isContactListHidden?: boolean;
  itemClassName?: string;
  sortable?: boolean;
  maxSelectCount?: number;
}

export interface ContactListRef {
  removeSelectedFriend(id: number): void;
}

export const ContactList = forwardRef<ContactListRef, Props>(
  (
    {
      friends,
      onSelectFriends,
      selectedFriendIds = [],
      singleSelect,
      disabledUserIds,
      isContactListHidden,
      itemClassName,
      sortable = true,
      maxSelectCount,
    },
    ref,
  ) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [sortBy, setSortBy] = useState<SortFriendReducerFilterType>('by-name');
    const { availableSessions: recentSessions } = useConversationSessions();

    const { isDesktop } = useLayoutStore();

    const sortedFriends = useMemo(
      () => sortFriendByFilterType(sortBy, friends, recentSessions),
      [sortBy, friends, recentSessions],
    );

    const { t } = useTranslation();

    useImperativeHandle(ref, () => ({
      removeSelectedFriend: (id) => {
        setSelectedIds((s) => s.filter((i) => i !== id));
      },
    }));

    function handleSelectFriend(friend: Friend) {
      if (disabledUserIds?.includes(friend.FriendID) || selectedFriendIds.includes(friend.FriendID)) return;

      if (singleSelect) {
        setSelectedIds([friend.FriendID]);
        return;
      }

      if (selectedIds.includes(friend.FriendID)) {
        // Remove friend from selection
        setSelectedIds((s) => s.filter((id) => id !== friend.FriendID));
      } else {
        // Add friend to selection
        if (maxSelectCount && selectedIds.length >= maxSelectCount) {
          addTranslateToast({
            type: 'warning',
            message: `conversation.forward.max|count=${maxSelectCount}`,
          });
          return;
        }
        setSelectedIds((s) => s.concat([friend.FriendID]));
      }
    }

    useEffect(() => {
      if (onSelectFriends) {
        onSelectFriends(selectedIds);
      }
    }, [selectedIds]);

    return (
      <>
        <div className={cn('flex mx-5 items-center justify-between h-6', isContactListHidden && 'hidden')}>
          <span className='text-sm font-bold'>{`${t('conversation.contact.contact')} (${sortedFriends.length})`}</span>
          {sortable ? (
            <SortContactDropdown
              sortBy={sortBy}
              setSortBy={setSortBy}
            />
          ) : (
            <div />
          )}
        </div>
        <Virtuoso
          className={cn('flex flex-1 flex-col items-stretch', isContactListHidden && 'hidden')}
          data={sortedFriends}
          overscan={6}
          defaultItemHeight={56}
          increaseViewportBy={168}
          itemContent={(_, friend) => {
            const isSelected = selectedIds.includes(friend.FriendID);
            const isSelectedFriend = selectedFriendIds.includes(friend.FriendID);
            const isDisabled = disabledUserIds?.includes(friend.FriendID);
            if (onSelectFriends)
              return (
                <div className={cn('group/contact', itemClassName)}>
                  <div
                    className={cn('flex items-center desktop:me-3 rounded-box', {
                      'group-hover/contact:[&_div]:bg-base-100': !isDisabled,
                    })}
                    onClick={() => handleSelectFriend(friend)}>
                    <ContactListItem
                      item={friend}
                      suffix={
                        <input
                          type='checkbox'
                          readOnly
                          disabled={isSelectedFriend || isDisabled}
                          checked={isSelected || isSelectedFriend}
                          className={cn('mr-2 ms-2 rounded-full border-base-200', {
                            'radio-primary radio': singleSelect,
                            'checkbox-primary checkbox': !singleSelect,
                          })}
                        />
                      }
                    />
                  </div>
                </div>
              );
            return (
              <div className={cn('group/contact', itemClassName)}>
                <NavLink
                  className={({ isActive }) =>
                    cn('group-hover/contact:[&_div]:bg-base-100 rounded-box', { '*:bg-base-100': isActive })
                  }
                  to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({ params: { userId: friend.FriendID } })}
                  onClick={async (e) => {
                    if (isDesktop) {
                      e.preventDefault();
                      if (friend?.ChatSessionID) await handleDisplaySessionMessages(friend.ChatSessionID);
                    }
                  }}
                  state={ROUTES.CONTACT.USERS.BY_ID.$buildState({ state: { from: 'contact' } })}>
                  <ContactListItem item={friend} />
                </NavLink>
              </div>
            );
          }}
        />
      </>
    );
  },
);

ContactList.displayName = 'ContactList';
